import { useTranslation } from 'react-i18next';

import type { Attraction } from '../../lib/types';

/**
 * AttractionDetailPanel — spec §25.3. Opened by "View Details"; there is no separate
 * attraction page (§20.1-16). image · name · subtitle · description · tag chips ·
 * Share Experience (blue) + Plan Trip (grey) · Tips bullets · ✕.
 *
 * Action behaviour follows the §20.2-3 defaults (unspecified in the design):
 *   - Share Experience → native share sheet, falling back to copy-link.
 *   - Plan Trip → open OpenStreetMap directions to the pin (OSM, not Google — §3/§20.1-2).
 */
export function AttractionDetailPanel({
  attraction,
  onClose,
}: {
  attraction: Attraction;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const share = async () => {
    const url = `${window.location.origin}${window.location.pathname}?attraction=${attraction.slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: attraction.name, url });
        return;
      } catch {
        // user dismissed the sheet — fall through to copy
      }
    }
    await navigator.clipboard?.writeText(url);
  };

  const planTrip = () => {
    if (attraction.latitude == null || attraction.longitude == null) return;
    window.open(
      `https://www.openstreetmap.org/directions?to=${attraction.latitude},${attraction.longitude}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <aside className="relative border-t border-line xl:border-l xl:border-t-0" aria-label={attraction.name}>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('a11y.closeModal')}
        className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-pill bg-panel/90 text-content shadow-soft backdrop-blur hover:bg-content/5"
      >
        <span aria-hidden="true">✕</span>
      </button>

      <div className="max-h-[40rem] overflow-y-auto">
        {attraction.image && (
          <img
            src={attraction.image}
            alt={attraction.name}
            className="h-48 w-full object-cover"
          />
        )}

        <div className="space-y-4 p-6">
          <div>
            <h3 className="text-lg font-bold text-content">{attraction.name}</h3>
            {attraction.subtitle && (
              <p className="text-sm text-content-muted">{attraction.subtitle}</p>
            )}
          </div>

          {attraction.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attraction.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-pill bg-content/5 px-3 py-1 text-xs text-content-muted"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {attraction.description && (
            <p className="text-sm leading-relaxed text-content">{attraction.description}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={share}
              className="rounded-pill bg-accent px-4 py-2 text-sm font-medium text-accent-contrast transition-opacity hover:opacity-90"
            >
              {t('attraction.share')}
            </button>
            <button
              type="button"
              onClick={planTrip}
              disabled={attraction.latitude == null}
              className="rounded-pill bg-content/5 px-4 py-2 text-sm font-medium text-content transition-colors hover:bg-content/10 disabled:opacity-40"
            >
              {t('attraction.planTrip')}
            </button>
          </div>

          {attraction.tips.length > 0 && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-content">{t('attraction.tips')}</h4>
              <ul className="list-disc space-y-1 pl-5 text-sm text-content-muted">
                {attraction.tips.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
